import { useCallback, useEffect, useRef, useState } from "react";

const initialState = { items: [], page: 0, pageCount: 0, loading: true, error: false };

// Fetch every page automatically; ignore responses from a previous profile/filter.
export default function useProfilePosts(fetchPage, userId, categoryId) {
  const [state, setState] = useState(initialState);
  const generation = useRef(0);
  const pending = useRef(null);

  const loadPage = useCallback(async (page, requestGeneration) => {
    if (!userId || pending.current === requestGeneration) return;
    pending.current = requestGeneration;
    setState(previous => ({ ...previous, loading: true, error: false }));
    try {
      let nextPage = page;
      while (generation.current === requestGeneration) {
        const result = await fetchPage({ userId, categoryId, pageNo: nextPage });
        if (generation.current !== requestGeneration) return;
        if (result.status !== "Success" || !Array.isArray(result.data) ||
            result.pageNo !== nextPage || !Number.isInteger(result.length) || result.length < 0) {
          throw new Error("Could not load profile posts");
        }
        const items = result.data.filter(item => item?.isDraft !== true);
        const isFirstPage = nextPage === 1;
        const hasMore = result.pageNo < result.length;
        setState(previous => {
          const combined = isFirstPage ? items : [...previous.items, ...items];
          const seen = new Set();
          return {
            items: combined.filter(item => {
              if (seen.has(item._id)) return false;
              seen.add(item._id);
              return true;
            }),
            page: result.pageNo,
            pageCount: result.length,
            loading: hasMore,
            error: false,
          };
        });
        if (!hasMore) break;
        nextPage = result.pageNo + 1;
      }
    } catch (error) {
      if (generation.current === requestGeneration) {
        setState(previous => ({ ...previous, loading: false, error: true }));
      }
    } finally {
      if (pending.current === requestGeneration) pending.current = null;
    }
  }, [fetchPage, userId, categoryId]);

  useEffect(() => {
    const requestGeneration = ++generation.current;
    setState(initialState);
    if (userId) {
      loadPage(1, requestGeneration);
    } else {
      setState({ ...initialState, loading: false });
    }
    return () => { generation.current += 1; };
  }, [loadPage, userId]);

  return {
    ...state,
    retry: () => loadPage(state.page + 1, generation.current),
  };
}
